/**
 * Live lessons v2.0 callables and reads (docs/live-lessons-data.md section
 * 6). The rules live on the server; these wrappers carry arguments in and
 * results out, and subscribe to the records the screens show.
 */
import { collection, doc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import type { LiveCallBooking, LiveMembership, LiveTimeRequest } from '../types/liveCall';
import type { RoomJoin } from '../utils/liveRoom';
import { chipLesson } from '../utils/lessonChip';

const call = <I, O>(name: string) => {
  const fn = httpsCallable<I, O>(functions, name);
  return async (input: I): Promise<O> => (await fn(input)).data;
};

export type LessonMode = 'match_review' | 'guidance_call';

// ── Moving a lesson ───────────────────────────────────────────────────────────

/** Request a New Time: one flow, either side. */
export const requestLiveNewTime =
  // toStartAt: DEV ONLY: LIVE_DEV_ANY_TIME, any future start on the slot grid (ISO).
  // Decision 123: the coach's only; a player's moves the lesson at once ({moved, startAt}).
  // startAts: the player's own times for a lesson (165, row 4; contracts 16), answered by the coach ({proposalId}).
  call<{ bookingId: string; toSlotId?: string; toStartAt?: string; startAts?: string[] },
    { requestId?: string; moved?: true; startAt?: string; proposalId?: string }>('requestLiveNewTime');
export const respondToLiveTimeRequest =
  call<{ requestId: string; accept: boolean }, { status: string }>('respondToLiveTimeRequest');
/** Adjust Time: ±5, 10, 15, 20 or 30 minutes, any time. */
export const adjustLiveLessonTime =
  call<{ bookingId: string; minutes: number }, { requestId: string; toStartAt: string }>('adjustLiveLessonTime');
export const respondToLiveAdjustment =
  call<{ requestId: string; accept: boolean }, { status: string }>('respondToLiveAdjustment');

/** The open requests and adjustments on the caller's lessons. */
export function subscribeOpenTimeRequests(
  uid: string,
  role: 'coach' | 'player',
  onChange: (requests: LiveTimeRequest[]) => void,
): () => void {
  const q = query(
    collection(db, 'liveTimeRequests'),
    where(role === 'coach' ? 'coachId' : 'playerId', '==', uid),
    where('status', '==', 'open'),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => ({ ...(d.data() as Omit<LiveTimeRequest, 'id'>), id: d.id }))),
    () => onChange([]),
  );
}

// ── Cancelling ────────────────────────────────────────────────────────────────

export interface LiveCancelPreview {
  bookingId: string;
  actor: 'coach' | 'player';
  kind: 'membership' | 'single' | 'intro';
  allowed: boolean;
  insideNotice: boolean;
  lateCancel: boolean;
  credit: 'returns' | 'used' | 'none';
  refund: 'full' | 'charged' | 'none';
  coachMayReturn: boolean;
  decisionDueAt: string | null;
  /** The sheet's one line, in the caller's words. */
  line: string;
}
export const getLiveCancelPreview =
  call<{ bookingId: string }, LiveCancelPreview>('getLiveCancelPreview');

// ── Thread cards ──────────────────────────────────────────────────────────────

/** Suggest a Time (coach) or Request a Time (member), from the thread. */
export const sendLiveTimeCard =
  call<{ membershipId: string; slotId: string; lessonMode?: LessonMode }, { cardId: string }>('sendLiveTimeCard');
export const acceptLiveTimeCard =
  call<{ cardId: string; lessonMode?: LessonMode }, { bookingId: string }>('acceptLiveTimeCard');

export interface LiveTimeCard {
  id: string;
  membershipId: string;
  coachId: string;
  playerId: string;
  sentBy: 'coach' | 'player';
  kind: 'suggest' | 'request';
  slotId: string;
  startAt: string;
  lessonMode?: LessonMode;
  status: 'open' | 'accepting' | 'accepted' | 'taken' | 'expired' | string;
  bookingId?: string;
}

export function subscribeTimeCard(cardId: string, onChange: (c: LiveTimeCard | null) => void): () => void {
  return onSnapshot(
    doc(db, 'liveTimeCards', cardId),
    (snap) => onChange(snap.exists() ? { ...(snap.data() as Omit<LiveTimeCard, 'id'>), id: snap.id } : null),
    () => onChange(null),
  );
}

// ── The coach's live offers ──────────────────────────────────────────────────

export interface LiveOffersState {
  enabled: boolean;
  introCall: boolean;
  singlePriceCents: number;
  membershipPriceCents: number;
  /** Percent off four lessons: 20 by default, 0 to 30 if the coach sets it. */
  membershipDiscountPercent: number;
  /** The coach set it; otherwise it follows the lesson price. */
  membershipPriceSet: boolean;
  spots: { cap: number | null; isDefault: boolean };
  offeringIds: { intro: string; single: string; membership: string };
}
/**
 * Turn live lessons on or off and set the three offers: the Free Intro Call
 * (on by default), the Live Video Lesson's price ($150 by default),
 * Monthly Coaching's ($450) and its spots (10 when never set; null for no
 * limit). Off hides them from new bookings; existing ones stand.
 */
export const setLiveOffers = call<{
  enabled?: boolean; introCall?: boolean; singlePriceCents?: number;
  membershipPriceCents?: number; spots?: number | null;
}, LiveOffersState>('setLiveOffers');

// ── Weekly time ───────────────────────────────────────────────────────────────

export const setLiveWeeklyTime = call<
  { membershipId: string; weekday: number; time: string; lessonMode: LessonMode },
  { weeklyTime: unknown; line: string; booked: number }
>('setLiveWeeklyTime');

/**
 * Remind Me under "Times open <date>": the player hears once when that day
 * opens. The date is the coach's, "2027-01-05": the first day past the
 * booking window.
 */
export const remindLiveTimesOpen =
  call<{ coachId: string; date: string }, { reminderId: string; date: string }>('remindLiveTimesOpen');

/** liveOpenReminders/{coachId}_{date}_{playerId}: whether this player has asked about that day. */
export function subscribeOpenReminder(
  coachId: string, date: string, playerId: string, onChange: (asked: boolean) => void,
): () => void {
  return onSnapshot(
    doc(db, 'liveOpenReminders', `${coachId}_${date}_${playerId}`),
    (snap) => onChange(snap.exists()),
    () => onChange(false),
  );
}

/** coachSchedules/{coachId}: the default week, in the coach's zone, on the half hour. */
export interface CoachWeek {
  timeZone: string;
  week: Record<string, Array<{ start: string; end: string }>>;
}

export function subscribeCoachWeek(coachId: string, onChange: (w: CoachWeek | null) => void): () => void {
  return onSnapshot(
    doc(db, 'coachSchedules', coachId),
    (snap) => {
      const d = snap.data();
      onChange(d ? { timeZone: d.timeZone, week: d.week ?? {} } : null);
    },
    () => onChange(null),
  );
}

// ── The coach's week and time off ────────────────────────────────────────────

/** A date's override state: the week's blocks, the coach's own, a day off, or time off. */
export type DayState = 'default' | 'edited' | 'off' | 'time_off';

export interface CoachWeekView extends CoachWeek {
  days: Array<{ date: string; hours: Array<{ start: string; end: string }>; source: 'default' | 'edited' | 'off' | 'time_off' }>;
  /** The next 21 days, today first: each date's effective blocks, state and booked lessons. */
  dates: Array<{
    date: string;
    weekday: number;
    blocks: Array<{ start: string; end: string }>;
    state: DayState;
    timeOffId?: string;
    lessons: Array<{
      bookingId: string; startAt: string; durationMinutes: number; status: string;
      playerId: string; playerName: string | null; offeringType: string; lessonMode: LessonMode | null;
    }>;
  }>;
  /** Monthly Coaching spots: 10 when never set; cap null is no limit. */
  spots: { cap: number | null; held: number; isDefault: boolean };
  /** The rest of the page, in the same response: the counts line, the offers and the time off ahead. */
  counts: LiveAvailabilityLine;
  offers: LiveOffersState;
  timeOff: Array<Pick<CoachTimeOff, 'id' | 'from' | 'to' | 'days'>>;
}
export const getLiveCoachWeek = call<Record<string, never>, CoachWeekView>('getLiveCoachWeek');
export const setLiveDefaultWeek = call<
  { week: Record<string, Array<{ start: string; end: string }>> },
  { week: CoachWeek['week']; opened: number }
>('setLiveDefaultWeek');
/** One date's override inside the window: its blocks, off, or reset to the week. */
export const setLiveDayHours = call<
  { date: string; hours?: Array<{ start: string; end: string }>; off?: boolean; reset?: boolean },
  { openTimes: number }
>('setLiveDayHours');
/** Add Time Off: the block, and the page after it (view, as getLiveCoachWeek). */
export const setLiveTimeOff = call<
  { from: string; to: string },
  { timeOffId: string; days: number; extended: boolean; released: string[]; view: CoachWeekView | null }
>('setLiveTimeOff');

export interface CoachTimeOff {
  id: string;
  coachId: string;
  from: string;
  to: string;
  days: number;
  /** A block the coach removed; its dates are back on the week. */
  status?: 'removed';
}

/** Remove a block of time off: its dates return to the week. */
/** Remove Time Off: what came back, and the page after it (view). */
export const removeLiveTimeOff = call<
  { timeOffId: string },
  { removed: true; restored: number; monthsRestored: number; lessonsBack: string[]; view: CoachWeekView | null }
>('removeLiveTimeOff');

/** The coach's time off that has not ended yet, and not removed. */
export function subscribeCoachTimeOff(coachId: string, todayKey: string, onChange: (t: CoachTimeOff[]) => void): () => void {
  const q = query(collection(db, 'coachTimeOff'), where('coachId', '==', coachId), where('to', '>=', todayKey));
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => ({ ...(d.data() as Omit<CoachTimeOff, 'id'>), id: d.id }))
      .filter((t) => t.status !== 'removed')
      .sort((a, b) => a.from.localeCompare(b.from))),
    () => onChange([]),
  );
}

// ── The room and after it ─────────────────────────────────────────────────────

/**
 * Done (decision 55): this person's confirmation that the lesson happened.
 * The server checks the join and finishes the lesson once both have
 * (decision 56). There is no End Lesson and no Reopen (decision 53).
 */
export const confirmLiveDone =
  // Decision 105: the player's Done can carry the rating, which closes the lesson.
  call<{ bookingId: string; happened?: boolean; rating?: { stars: number; text?: string } },
    { done: true; finished: boolean; rated?: boolean }>('confirmLiveDone');
export const answerLiveAttendance = call<
  { bookingId: string; showedUp: boolean },
  { attendancePrompt: { coach?: string; player?: string } }
>('answerLiveAttendance');
export const reportLiveLessonProblem =
  call<{ bookingId: string; note?: string }, { reported: true }>('reportLiveLessonProblem');
export const returnLiveLesson =
  call<{ bookingId: string }, { missDecision: 'returned' }>('returnLiveLesson');

// ── Coach money ───────────────────────────────────────────────────────────────

export interface MonthlyCoachingSummaryRow {
  membershipId: string;
  playerId: string;
  playerName: string;
  playerAvatarUrl: string | null;
  state: 'first_month' | 'ongoing' | 'away' | 'failed' | 'cancelled';
  line: string;
  returned: string[];
}
export interface MonthlyCoachingSummary {
  line: string;
  totalCents: number;
  members: number;
  rows: MonthlyCoachingSummaryRow[];
}
export const getLiveMonthlyCoachingSummary =
  call<{ coachId?: string }, MonthlyCoachingSummary>('getLiveMonthlyCoachingSummary');

// ── A Monthly Coaching month ──────────────────────────────────────────────────

/**
 * The held lessons of one Monthly Coaching (decision 89; liveWeeklyHolds/{membershipId}_{date}): the starts
 * still held, not booked yet. Read by either side of the pair, as the rules allow.
 */
export function subscribeWeeklyHolds(
  uid: string,
  role: 'coach' | 'player',
  membershipId: string,
  onChange: (starts: string[]) => void,
): () => void {
  const q = query(
    collection(db, 'liveWeeklyHolds'),
    where(role === 'coach' ? 'coachId' : 'playerId', '==', uid),
    where('membershipId', '==', membershipId),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => d.data()).filter((h) => h.status === 'held').map((h) => h.startAt as string).sort()),
    () => onChange([]),
  );
}

/**
 * Billing history's live sources (audit 2 Nov, A17): the player's live
 * bookings, their Monthly Coaching plans and the billing events the server
 * keeps for them (liveBillingEvents). Each read fails alone, to nothing.
 */
export async function getLiveBillingSources(uid: string): Promise<{
  bookings: LiveCallBooking[]; memberships: LiveMembership[];
  events: { id: string; kind: string; membershipId?: string | null; at: string }[];
}> {
  const read = async <T>(col: string): Promise<T[]> => {
    try {
      const snap = await getDocs(query(collection(db, col), where('playerId', '==', uid)));
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as T);
    } catch {
      return [];
    }
  };
  const [bookings, memberships, events] = await Promise.all([
    read<LiveCallBooking>('liveCallBookings'), read<LiveMembership>('liveMemberships'),
    read<{ id: string; kind: string; membershipId?: string | null; at: string }>('liveBillingEvents'),
  ]);
  return { bookings, memberships, events };
}

/** The same holds with their ids, for the coach's move of one (answerLiveWeeklyHold). */
export function subscribeWeeklyHoldDocs(
  uid: string,
  role: 'coach' | 'player',
  membershipId: string,
  onChange: (holds: Array<{ id: string; startAt: string }>) => void,
): () => void {
  const q = query(
    collection(db, 'liveWeeklyHolds'),
    where(role === 'coach' ? 'coachId' : 'playerId', '==', uid),
    where('membershipId', '==', membershipId),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.filter((d) => d.data().status === 'held')
      .map((d) => ({ id: d.id, startAt: d.data().startAt as string })).sort((x, y) => x.startAt.localeCompare(y.startAt))),
    () => onChange([]),
  );
}

/** The coach moves a held lesson to another time (functions weekly.ts answerWeeklyHold, answer "move"). */
export const answerLiveWeeklyHold =
  call<{ holdId: string; answer: 'confirm' | 'move'; startAt?: string }, { status: string; startAt: string; bookingId: string | null }>('answerLiveWeeklyHold');

/** Decision 154: the coach decides a player no-show (booking-contracts.md 13). */
export const resolvePlayerNoShow =
  call<{ bookingId: string; choice: 'count' | 'offer'; requestId?: string }, { status: 'counted' | 'offered' }>('resolvePlayerNoShow');

/** Every lesson of one Monthly Coaching, cancelled ones included, for its month. */
export function subscribeMembershipLessons(
  uid: string,
  role: 'coach' | 'player',
  membershipId: string,
  onChange: (bookings: LiveCallBooking[]) => void,
): () => void {
  const q = query(
    collection(db, 'liveCallBookings'),
    where(role === 'coach' ? 'coachId' : 'playerId', '==', uid),
    where('membershipId', '==', membershipId),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => ({ ...(d.data() as LiveCallBooking), id: d.id }))),
    () => onChange([]),
  );
}

/** The next confirmed lesson between the signed-in person and another, for the thread's Adjust Time chip. */
export function subscribeNextLessonWith(
  uid: string,
  otherId: string,
  onChange: (b: LiveCallBooking | null) => void,
): () => void {
  // The earliest lesson still to come, or, for its coach, still to answer
  // after its end ("Did this lesson happen?"; utils/lessonChip).
  const pick = (docs: LiveCallBooking[]) => chipLesson(docs, uid, Date.now());
  let asCoach: LiveCallBooking[] = [];
  let asPlayer: LiveCallBooking[] = [];
  const emit = () => onChange(pick([...asCoach, ...asPlayer]));
  const watch = (role: 'coach' | 'player') => onSnapshot(
    query(collection(db, 'liveCallBookings'),
      where(role === 'coach' ? 'coachId' : 'playerId', '==', uid),
      where(role === 'coach' ? 'playerId' : 'coachId', '==', otherId)),
    (snap) => {
      const docs = snap.docs.map((d) => ({ ...(d.data() as LiveCallBooking), id: d.id }));
      if (role === 'coach') asCoach = docs; else asPlayer = docs;
      emit();
    },
    () => { /* none */ },
  );
  const a = watch('coach');
  const b = watch('player');
  return () => { a(); b(); };
}

/**
 * The player's Monthly Coaching for its page: active, ending, paused, and a
 * lapsed one while its locked price can still be kept (lockedRate.until).
 */
export function subscribePlayerMonthlyCoaching(
  uid: string,
  onChange: (ms: LiveMembership[]) => void,
): () => void {
  const q = query(collection(db, 'liveMemberships'), where('playerId', '==', uid),
    where('status', 'in', ['active', 'cancelling', 'paused', 'cancelled']));
  return onSnapshot(q, (snap) => {
    const now = Date.now();
    onChange(snap.docs
      .map((d) => ({ ...(d.data() as LiveMembership), id: d.id }))
      .filter((m) => m.status !== 'cancelled' ||
        (!!m.lapsedAt && !!m.lockedRate && new Date(m.lockedRate.until).getTime() > now)));
  }, () => onChange([]));
}

/** Whether a cancelled Match Review left its match ready on this Monthly Coaching (liveMatchUploads). */
export function subscribeReadyMatch(uid: string, membershipId: string, onChange: (ready: boolean) => void): () => void {
  const q = query(collection(db, 'liveMatchUploads'), where('playerId', '==', uid),
    where('membershipId', '==', membershipId), where('status', '==', 'ready'));
  return onSnapshot(q, (snap) => onChange(!snap.empty), () => onChange(false));
}

/** The coach's Availability counts and lines (getLiveAvailabilityLine). */
export interface LiveAvailabilityLine {
  openSlots: number;
  membersUnusedCredits: number;
  members: number;
  cap: number | null;
  spotsLeft: number | null;
  until: string;
  membersBehind: number;
  line: string;
  membersLine: string;
}
export const getLiveAvailabilityLine = call<Record<string, never>, LiveAvailabilityLine>('getLiveAvailabilityLine');

// ── The pair's room (rule 9) ──────────────────────────────────────────────────

/** Join the pair's room: the lesson in its window, a guest's lesson, or a call. */
export const joinLiveRoom = call<{ roomId: string; token?: string }, RoomJoin>('joinLiveRoom');
/** The other side accepts a call outside a lesson, from the thread. */
export const acceptLiveRoomCall =
  call<{ roomId: string }, { kind: 'call'; recorded: false; connected: boolean }>('acceptLiveRoomCall');

/** liveRoomCalls/{roomId}: a call outside every window. */
export interface LiveRoomCall {
  coachId: string;
  playerId: string;
  status: 'waiting' | 'connected';
  requestedBy: 'coach' | 'player';
  expiresAt: string;
}

export function subscribeRoomCall(roomId: string, onChange: (c: LiveRoomCall | null) => void): () => void {
  return onSnapshot(doc(db, 'liveRoomCalls', roomId),
    (snap) => onChange(snap.exists() ? (snap.data() as LiveRoomCall) : null),
    () => onChange(null));
}

/** After a lesson's end, the coach's "Did this lesson happen?": Yes finishes it, No takes the no-show path. */
export const answerLiveLessonHappened =
  call<{ bookingId: string; happened: boolean }, { finished: boolean; status?: string; disputed?: true }>('answerLiveLessonHappened');

/** The lesson's guest link, made now for a lesson booked before links came with the booking. */
export const getLiveLessonLink = call<{ bookingId: string }, { token: string }>('getLiveLessonLink');
